import React, { useState, useEffect, useMemo, useCallback  } from "react";
import {
  AiOutlineSchedule,
  AiOutlineHourglass,
  AiOutlineCheckCircle,
  AiOutlineClockCircle,
  AiFillPlayCircle,
  AiOutlineQuestionCircle,
  AiOutlineLoading3Quarters,
} from "react-icons/ai";
import { useUser } from "../../../context/UserContext";
import { useAgreement } from "../../../context/AgreementContext";
import { useSession } from "../../../context/SessionContext";
import { useSessionSocket } from "../../../websockets/SessionSocketService";
import {
  useUserId,
  useUserRole,
  formatDateStringNoDay,
  formatDuration,
} from "../../../util_functions/helpers";

export default function SessionStartCard() {
  const userId = useUserId();
  const userType = useUserRole();
  const { agreements, listAgreementsByUser } = useAgreement();
  const {
    requestSessionStart,
    cancelSessionStart,
    completeSession,
  } = useSession();

  const handleSessionUpdate = useCallback(
    (updated) => {
      if (userType && userId) {
        listAgreementsByUser(userType, userId).catch(console.error);
      }
    },
    [userType, userId, listAgreementsByUser]  
  );

  useSessionSocket(handleSessionUpdate);

  useEffect(() => {
    if (!userType || !userId) return;
    listAgreementsByUser(userType, userId).catch(console.error);
  }, [listAgreementsByUser, userType, userId]);

  const sessionInfos = useMemo(() => {
    const infos = [];
    for (const ag of agreements || []) {
      const sessions = ag.agreementSessions || [];
      for (const s of sessions) {
        if (s.requestedStartAt && s.status === "REQUESTED") {
          infos.push({ phase: "REQUESTED", agreement: ag, session: s });
        } else if (
          s.approvedStartAt &&
          s.status === "APPROVED" &&
          ag.executionStage === "ACTIVE"
        ) {
          infos.push({ phase: "ACTIVE", agreement: ag, session: s });
        } else if (s.status === "PENDING") {
          infos.push({ phase: "PENDING", agreement: ag, session: s });
        }
      }
    }
    infos.sort((a, b) => {
      const aTime = a.session.requestedStartAt || a.session.scheduledStart;
      const bTime = b.session.requestedStartAt || b.session.scheduledStart;
      return bTime - aTime;
    });
    return infos;
  }, [agreements]);

  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);

  const handleStart = async (agreementId) => {
    try {
      await requestSessionStart(agreementId);
    } catch (err) {
      console.error(err);
    }
  };
  const handleCancel = async (sessionId) => {
    try {
      await cancelSessionStart(sessionId);
    } catch (err) {
      console.error(err);
    }
  };
  const handleEnd = async (sessionId) => {
    try {
      await completeSession(sessionId);
    } catch (err) {
      console.error(err);
    }
  };

  if (sessionInfos.length === 0) return null;

  return (
    <>
      {sessionInfos.map(({ phase, agreement, session }) => {
        const { workerName, jobTitle } = agreement;
        const { sessionId, scheduledStart, requestedStartAt, approvedStartAt } = session;
        const isASAP = scheduledStart === 0;
        const scheduledDisplay = isASAP ? (
          <strong>As soon as possible</strong>
        ) : (
          formatDateStringNoDay(scheduledStart)
        );

        const elapsed =
          phase === "ACTIVE"
            ? Math.floor((now - approvedStartAt) / 1000)
            : 0;
        const elapsedLabel = phase === "ACTIVE" ? formatDuration(elapsed) : null;

        return (
          <div key={sessionId} className="session-start-card">
            <h3>
              {phase === "ACTIVE" ? (
                <AiFillPlayCircle className="session-icon active-icon" />
              ) : phase === "REQUESTED" ? (
                <AiOutlineHourglass className="session-icon requested-icon" />
              ) : (
                <AiOutlineQuestionCircle className="session-icon pending-icon" />
              )}{" "}
              {phase === "ACTIVE"
                ? "Session In Progress"
                : phase === "REQUESTED"
                ? "Session Approval Pending"
                : "Upcoming Session"}
            </h3>

            <div className="card-body">
              <p>
                <span className="label">Worker</span>{" "}
                <strong>{workerName}</strong>
              </p>
              <p>
                <span className="label">Job</span> {jobTitle}
              </p>

              {(phase === "PENDING" || phase === "REQUESTED") && (
                <p>
                  <span className="label">Start time</span> {scheduledDisplay}
                </p>
              )}

              {phase === "ACTIVE" && (
                <>
                  <p>
                    <span className="label">Started</span>{" "}
                    <strong>{formatDateStringNoDay(approvedStartAt)}</strong>
                  </p>
                  <p>
                    <span className="label">Elapsed</span>{" "}
                    <strong>{elapsedLabel}</strong>
                  </p>
                </>
              )}
            </div>

            <div className="modal-actions">
              {phase === "PENDING" && (
                <button
                  className="btn start"
                  onClick={() => handleStart(agreement.agreementId)}
                >
                  Start Session
                </button>
              )}
              {phase === "REQUESTED" && (
                <>
                  <button className="btn" disabled>
                    <AiOutlineLoading3Quarters className="waiting-icon" />
                    Awaiting Approval
                  </button>
                  <button
                    className="btn reject"
                    onClick={() => handleCancel(sessionId)}
                  >
                    Cancel Request
                  </button>
                </>
              )}
              {phase === "ACTIVE" && (
                <button
                  className="btn end"
                  onClick={() => handleEnd(sessionId)}
                >
                  End Session
                </button>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}
